// Utilitários para trabalhar com URLs do YouTube

/**
 * Extrai o ID do vídeo do YouTube de diferentes formatos de URL
 */
export function extractYouTubeVideoId(url: string): string | null {
  try {
    const urlObj = new URL(url);
    
    // Para URLs do tipo youtube.com/watch?v=ID
    if (urlObj.hostname.includes('youtube.com') && urlObj.searchParams.has('v')) {
      return urlObj.searchParams.get('v');
    }
    
    // Para URLs do tipo youtu.be/ID
    if (urlObj.hostname === 'youtu.be') {
      return urlObj.pathname.slice(1); // Remove a primeira barra
    }
    
    // Para URLs do tipo youtube.com/embed/ID
    if (urlObj.hostname.includes('youtube.com') && urlObj.pathname.startsWith('/embed/')) {
      return urlObj.pathname.split('/embed/')[1];
    }
    
    return null;
  } catch {
    return null;
  }
}

/**
 * Verifica se uma URL é do YouTube
 */
export function isYouTubeUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname === "youtube.com" || 
           urlObj.hostname === "www.youtube.com" || 
           urlObj.hostname === "youtu.be" ||
           urlObj.hostname === "m.youtube.com";
  } catch {
    return false;
  }
}

/**
 * Converte uma URL do YouTube para URL de embed sem controles e em tela cheia
 */
export function getYouTubeEmbedUrl(url: string): string | null {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) return null;
  
  // Parâmetros otimizados para carregamento instantâneo e performance máxima
  const params = new URLSearchParams({
    autoplay: '1',
    mute: '1',
    controls: '0',
    showinfo: '0',
    rel: '0',
    iv_load_policy: '3',
    modestbranding: '1',
    playsinline: '1',
    loop: '1',
    playlist: videoId, // Necessário para o loop funcionar
    fs: '0', // Desabilita fullscreen button
    cc_load_policy: '0', // Desabilita closed captions
    disablekb: '1', // Desabilita keyboard controls
    enablejsapi: '0', // Desabilita JavaScript API
    origin: window.location.origin, // Define origem para melhor integração
    widget_referrer: window.location.href, // Referrer para analytics
    start: '0', // Força início do vídeo
    wmode: 'opaque', // Modo de window para melhor integração
    // Parâmetros de performance para carregamento instantâneo
    preload: 'auto', // Pré-carregamento automático
    vq: 'hd1080', // Força qualidade HD para dispositivos compatíveis
    hl: 'pt', // Idioma português para otimização regional
    cc_lang_pref: 'pt', // Preferência de idioma
    html5: '1', // Força player HTML5
    fmt: '18' // Formato otimizado para streaming rápido
  });
  
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
}

/**
 * Gera URL da thumbnail do YouTube
 */
export function getYouTubeThumbnailUrl(url: string): string | null {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) return null;
  
  // Usar thumbnail de alta qualidade
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
}

/**
 * Verifica se uma URL é de mídia tradicional (imagem/vídeo)
 */
export function isTraditionalMediaUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    const isLocaNinja = urlObj.hostname === "locaninja.com" || urlObj.hostname.endsWith(".locaninja.com");
    
    if (isLocaNinja) {
      const pathname = urlObj.pathname.toLowerCase();
      return pathname.endsWith('.png') || 
             pathname.endsWith('.jpg') || 
             pathname.endsWith('.jpeg') || 
             pathname.endsWith('.gif') ||
             pathname.endsWith('.webp') ||
             pathname.endsWith('.mp4') ||
             pathname.endsWith('.webm') ||
             pathname.endsWith('.mov');
    }
    
    return false;
  } catch {
    return false;
  }
}
